import {Component, computed, effect, EffectRef, inject, OnDestroy, signal} from '@angular/core';
import {WindowRefService} from "../../shared/helpers/window-ref/window-ref.service";
import {NgIf} from "@angular/common";
import {RuntimeService} from "../../core/services/runtime.service";

@Component({
    standalone: true,
    selector: 'app-consent-banner',
    imports: [NgIf],
    templateUrl: './consent-banner.component.html',
    styleUrls: ['./consent-banner.component.scss']
})
export class ConsentBannerComponent implements OnDestroy {
    // Signal to track consent state
    consentGiven = signal<boolean | null>(null);
    // Computed signal to determine if the banner should be shown
    showBanner = computed(() => this.consentGiven() === null);
    private readonly effectCleanup!: EffectRef; // Use definite assignment assertion
    readonly #runtimeService = inject(RuntimeService);
    readonly isBrowser = this.#runtimeService.isBrowser;

    constructor(private windowRef: WindowRefService) {
        if (this.isBrowser) {
            // Set up the effect to apply consent settings when consentGiven changes
            this.effectCleanup = effect(() => {
                const consent = this.consentGiven();
                if (consent !== null) {
                    this.applyConsentSettings(consent);
                }
            });
            // Initialize consent synchronously on the client
            this.initializeConsent();
        }
    }

    ngOnDestroy() {
        // Clean up the effect when the component is destroyed
        if (this.effectCleanup) {
            this.effectCleanup.destroy();
        }
    }

    acceptCookies(): void {
        if (this.isBrowser) {
            this.updateConsent(true);
        }
    }

    rejectCookies(): void {
        if (this.isBrowser) {
            this.updateConsent(false);
        }
    }

    private initializeConsent(): void {
        if (this.isBrowser) {
            const storedConsent = localStorage.getItem('analytics_consent');
            this.consentGiven.set(
                storedConsent === 'true' ? true :
                    storedConsent === 'false' ? false : null
            );
        }
    }

    private updateConsent(consent: boolean): void {
        if (this.isBrowser) {
            localStorage.setItem('analytics_consent', consent ? 'true' : 'false');
            this.consentGiven.set(consent); // Update the signal
        }
    }

    private applyConsentSettings(consentGiven: boolean): void {
        if (this.isBrowser) {
            const gtag = this.windowRef.nativeWindow?.['gtag'];
            if (gtag) {
                gtag('consent', 'update', {
                    'ad_user_data': consentGiven ? 'granted' : 'denied',
                    'ad_personalization': consentGiven ? 'granted' : 'denied',
                    'ad_storage': consentGiven ? 'granted' : 'denied',
                    'analytics_storage': consentGiven ? 'granted' : 'denied'
                });
            }
        }
    }
}
